import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import './Hosting.css';

const customerLabel = (customer) => {
  if (!customer) return '—';
  const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ');
  return customer.companyName || name || customer.email;
};

const formatDate = (date) => (date ? new Date(date).toLocaleDateString('en-GB') : 'N/A');

function Hosting() {
  const navigate = useNavigate();

  const [linked, setLinked] = useState([]);
  const [unlinked, setUnlinked] = useState([]);
  const [whmConfigured, setWhmConfigured] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState('');
  const [bannerType, setBannerType] = useState('success');
  const showSuccess = (text) => { setBannerType('success'); setBanner(text); };
  const showError = (text) => { setBannerType('error'); setBanner(text); };

  const [linkTarget, setLinkTarget] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [linking, setLinking] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [linkedData, unlinkedData, customerData] = await Promise.all([
        apiClient.get('/api/hosting-accounts').catch(() => []),
        apiClient.get('/api/hosting-accounts/unlinked').catch(() => ({ configured: true, accounts: [] })),
        apiClient.post('/api/users/findall', {}).catch(() => [])
      ]);
      setLinked(linkedData || []);
      setWhmConfigured(unlinkedData?.configured !== false);
      setUnlinked(unlinkedData?.accounts || []);
      setCustomers(customerData || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openLinkModal = (account) => {
    setLinkTarget(account);
    // Contact email on the WHM account is the strongest signal for which
    // customer it belongs to - pre-select a match rather than making staff
    // hunt through the full customer list every time.
    const match = account.contactemail
      ? customers.find((c) => (c.email || '').toLowerCase() === account.contactemail.toLowerCase())
      : null;
    setSelectedCustomerId(match ? String(match.id) : '');
  };

  const submitLink = async (e) => {
    e.preventDefault();
    if (!linkTarget || !selectedCustomerId) return;

    setLinking(true);
    try {
      const account = await apiClient.post('/api/hosting-accounts/link', {
        user_id: selectedCustomerId,
        cpanel_username: linkTarget.cpanel_username,
        domain_name: linkTarget.domain_name,
        contactemail: linkTarget.contactemail,
        whm_package: linkTarget.whm_package,
        status: linkTarget.status
      });
      const customer = customers.find((c) => String(c.id) === String(selectedCustomerId));
      setLinked((prev) => [{ ...account, customer }, ...prev]);
      setUnlinked((prev) => prev.filter((a) => a.cpanel_username !== linkTarget.cpanel_username));
      showSuccess(`${linkTarget.domain_name || linkTarget.cpanel_username} linked to ${customerLabel(customer)}.`);
      setLinkTarget(null);
    } catch (error) {
      console.error('Error linking hosting account:', error);
      showError(error.message || 'Could not link this hosting account.');
    } finally {
      setLinking(false);
    }
  };

  const refreshStatus = async (account) => {
    setRefreshingId(account.id);
    try {
      const updated = await apiClient.post(`/api/hosting-accounts/${account.id}/refresh`);
      setLinked((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
    } catch (error) {
      showError(error.message || 'Could not refresh this hosting account.');
    } finally {
      setRefreshingId(null);
    }
  };

  const filteredCustomers = useMemo(() => {
    // Cheap enough to just sort by label - customer lists here run in the
    // low hundreds, not something that needs indexing.
    return [...customers].sort((a, b) => customerLabel(a).localeCompare(customerLabel(b)));
  }, [customers]);

  const unlinkedColumns = [
    {
      key: 'domain_name',
      label: 'Domain',
      sortable: true,
      render: (row) => (
        <div>
          <div className="hosting-domain">{row.domain_name || '—'}</div>
          <div className="hosting-username">{row.cpanel_username}</div>
        </div>
      )
    },
    { key: 'contactemail', label: 'Contact Email', sortable: true, render: (row) => row.contactemail || '—' },
    { key: 'whm_package', label: 'Package', sortable: true, render: (row) => row.whm_package || '—' },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => <span className={`status-badge status-${row.status}`}>{row.status === 'suspended' ? 'Suspended' : 'Active'}</span>
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <Button onClick={(e) => { e.stopPropagation(); openLinkModal(row); }}>
          <i className="fas fa-link"></i> Link to Customer
        </Button>
      )
    }
  ];

  const linkedColumns = [
    {
      key: 'domain_name',
      label: 'Domain',
      sortable: true,
      render: (row) => (
        <div>
          <div className="hosting-domain">{row.domain_name}</div>
          <div className="hosting-username">{row.cpanel_username}</div>
        </div>
      )
    },
    {
      key: 'customer',
      label: 'Customer',
      sortable: true,
      sortValue: (row) => customerLabel(row.customer),
      render: (row) => (row.customer ? <Link to={`/customer/${row.customer.id}`}>{customerLabel(row.customer)}</Link> : '—')
    },
    { key: 'whm_package', label: 'Package', sortable: true, render: (row) => row.whm_package || '—' },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => <span className={`status-badge status-${row.status}`}>{row.status === 'suspended' ? 'Suspended' : 'Active'}</span>
    },
    { key: 'createdAt', label: 'Linked', sortable: true, render: (row) => formatDate(row.createdAt) },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <Button variant="secondary" onClick={(e) => { e.stopPropagation(); refreshStatus(row); }} disabled={refreshingId === row.id}>
          <i className="fas fa-rotate"></i> {refreshingId === row.id ? 'Refreshing...' : 'Refresh'}
        </Button>
      )
    }
  ];

  return (
    <PageShell>
      <div className="hosting-header">
        <div>
          <h1>Hosting</h1>
          <p className="hosting-intro">
            cPanel accounts that exist on the server but aren't yet tied to a customer record show up
            below - link each one to the customer it actually belongs to.
          </p>
        </div>
      </div>

      {banner && (
        <div className={`hosting-banner hosting-banner-${bannerType}`} role={bannerType === 'error' ? 'alert' : 'status'}>
          {banner}
          <button type="button" className="hosting-banner-close" onClick={() => setBanner('')} aria-label="Dismiss">&times;</button>
        </div>
      )}

      {!whmConfigured && (
        <div className="hosting-banner hosting-banner-error" role="alert">
          WHM is not configured on this instance (WHM_HOST / WHM_USER / WHM_API_TOKEN) - accounts on the
          server can't be listed until it is. Already-linked accounts below are unaffected.
        </div>
      )}

      {loading ? (
        <p>Loading hosting accounts...</p>
      ) : (
        <>
          <section className="hosting-section">
            <h2>Needs Linking {unlinked.length > 0 && <span className="hosting-count">{unlinked.length}</span>}</h2>
            <ResponsiveTable
              columns={unlinkedColumns}
              rows={unlinked}
              rowKey="cpanel_username"
              emptyMessage={whmConfigured ? 'Every cPanel account on the server is already linked to a customer.' : ''}
            />
          </section>

          <section className="hosting-section">
            <h2>Linked Accounts</h2>
            <ResponsiveTable
              columns={linkedColumns}
              rows={linked}
              rowKey="id"
              emptyMessage="No hosting accounts linked to a customer yet."
            />
          </section>
        </>
      )}

      <Modal
        open={!!linkTarget}
        onClose={() => setLinkTarget(null)}
        title={`Link ${linkTarget?.domain_name || linkTarget?.cpanel_username || ''}`}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setLinkTarget(null)}>Cancel</Button>
            <Button type="submit" form="link-hosting-form" disabled={linking || !selectedCustomerId}>
              {linking ? 'Linking...' : 'Link Account'}
            </Button>
          </>
        )}
      >
        <form id="link-hosting-form" onSubmit={submitLink}>
          <div className="hosting-link-summary">
            <span>Username: {linkTarget?.cpanel_username}</span>
            <span>Package: {linkTarget?.whm_package || '—'}</span>
            <span>WHM contact email: {linkTarget?.contactemail || '—'}</span>
          </div>
          <FormField id="link-customer" label="Customer" required>
            <select id="link-customer" value={selectedCustomerId} onChange={(e) => setSelectedCustomerId(e.target.value)}>
              <option value="">Select a customer</option>
              {filteredCustomers.map((c) => (
                <option key={c.id} value={c.id}>{customerLabel(c)} ({c.email})</option>
              ))}
            </select>
          </FormField>
        </form>
      </Modal>
    </PageShell>
  );
}

export default Hosting;
